/** The QR printed on a ໃບທະບຽນວິສາຫະກິດ carries a verification URL into the MOIC
 *  enterprise registry. This module turns that payload into the three parameters
 *  the registry's public verify endpoint needs — and refuses everything else.
 *
 *  A QR is untrusted input: a forged certificate can point its code at any host
 *  it likes. The target is therefore pinned here rather than taken from the
 *  payload, so a request is only ever made to the registry itself.
 */

const HOST = 'ebrs.moic.gov.la';
const ROUTE = '/printoutQRCodeVerification';

export interface VerifyParams {
  roId: string;
  id: string;
  sig: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** URL-safe base64, as the registry emits it (~344 chars in practice). */
const SIG = /^[A-Za-z0-9_-]{16,4096}$/;

export function parseVerifyUrl(payload: string): VerifyParams | null {
  let url: URL;
  try {
    url = new URL(payload.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.hostname !== HOST) return null;

  // The registry is a hash-routed SPA: both its route and its parameters live in
  // the fragment, so `url.searchParams` is empty and the query has to be split
  // out of the hash by hand.
  const hash = url.hash.slice(1);
  const q = hash.indexOf('?');
  if (q < 0 || hash.slice(0, q) !== ROUTE) return null;

  const params = new URLSearchParams(hash.slice(q + 1));
  const roId = params.get('roId') ?? '';
  const id = params.get('Id') ?? '';
  const sig = params.get('s') ?? '';
  if (!UUID.test(roId) || !UUID.test(id) || !SIG.test(sig)) return null;

  return { roId, id, sig };
}
